import { logger } from '../services/logger.ts';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff, UserPlus } from 'lucide-react';
import { Turnstile, type TurnstileRef } from '../components/common/Turnstile.tsx';

// Define special characters regex pattern
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{};':|<>?,./`~]/;

export function UserRegistration() {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileRef>(null);

  // Extract affiliate code from URL on component mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setAffiliateCode(refCode);
      logger.info('Affiliate code detected:', refCode);
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check minimum length
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter (a-z)');
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter (A-Z)');
    }
    
    // Check for digit
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number (0-9)');
    }
    
    // Check for special character
    if (!SPECIAL_CHARS_REGEX.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (SPECIAL_CHARS_REGEX.test(password)) strength++;
    
    if (strength <= 2) return { strength: 1, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Fair', color: 'bg-wayja-teal' };
    if (strength <= 5) return { strength: 3, label: 'Good', color: 'bg-yellow-500' };
    return { strength: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    // Use the new password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors.join('. ');
      }
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Skip captcha validation during form validation
    // Captcha will be executed automatically if needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Wait for Turnstile verification
    if (!captchaToken) {
      setLoading(false);
      setErrors(prev => ({ ...prev, captcha: 'Please complete the security verification above before creating your account.' }));
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.submit; // Remove the submit error instead of setting to null
      return newErrors;
    });
    
    try {
      // Import supabase client
      const { supabase } = await import('../lib/db.ts');
      
      // Sign up the user directly with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
          }
        }
      });
      
      if (error) {
        logger.error('Registration error:', error);
        throw error;
      }

      logger.info('Registration successful:', {
        userId: data.user?.id,
        email: data.user?.email,
        confirmationSent: data.user?.confirmation_sent_at
      });
      
      // If there's an affiliate code and user was created, register it
      if (affiliateCode && data.user) {
        try {
          const { data: affiliateData, error: affiliateError } = await supabase.rpc('register_with_affiliate_code', {
            p_user_id: data.user.id,
            p_affiliate_code: affiliateCode
          });

          if (affiliateError) {
            logger.error('Failed to register affiliate code:', affiliateError);
          } else if (affiliateData?.success) {
            logger.info('Successfully registered with affiliate code');
          }
        } catch (affiliateError) {
          logger.error('Error registering affiliate code:', affiliateError);
          // Don't fail the registration if affiliate code fails
        }
      }

      // Show success message
      setSuccess(true);
    } catch (error) {
      logger.error('Registration error:', error);
      setErrors(prev => ({
        ...prev,
        submit: error instanceof Error ? error.message : 'Registration failed. Please try again later.'
      }));
      // Reset CAPTCHA on error
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h1>
          <p className="text-gray-600">Join Wayja and start trading</p>
          {affiliateCode && (
            <div className="mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-sm text-blue-700">
                <UserPlus className="w-4 h-4" />
                <span>You were invited with affiliate code: <strong>{affiliateCode}</strong></span>
              </div>
            </div>
          )}
        </div>

        {success ? (
          <div className="space-y-6">
            <div className="bg-green-50 border-2 border-green-400 rounded-lg p-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email!</h2>
                  <p className="text-gray-700 mb-4">
                    We've sent a verification link to:
                  </p>
                  <p className="text-lg font-semibold text-green-700 mb-4">
                    {formData.email}
                  </p>
                  <div className="bg-white border border-green-200 rounded-lg p-4 text-left space-y-2">
                    <p className="text-sm text-gray-600">
                      <strong>Next steps:</strong>
                    </p>
                    <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                      <li>Open your email inbox</li>
                      <li>Click the verification link in the email</li>
                      <li>Return here to log in</li>
                    </ol>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Didn't receive the email? Check your spam folder or contact support at support@wayja.me.
                  </p>
                </div>
                <Link
                  to="/auth/login"
                  className="w-full bg-wayja-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                >
                  Go to Login Page
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {errors.submit}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`pl-10 w-full rounded-md border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                      placeholder="John"
                      disabled={loading}
                    />
                  </div>
                  {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`pl-10 w-full rounded-md border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                      placeholder="Doe"
                      disabled={loading}
                    />
                  </div>
                  {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`pl-10 w-full rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="john.doe@example.com"
                    disabled={loading}
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`pl-10 w-full rounded-md border ${errors.phone ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="+27 12 345 6789"
                    disabled={loading}
                  />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 pr-10 w-full rounded-md border ${errors.password ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                
                {/* Password strength indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Password strength</span>
                      <span className="text-xs font-medium text-gray-700">{getPasswordStrength(formData.password).label}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrength(formData.password).color}`}
                        style={{ width: `${getPasswordStrength(formData.password).strength * 25}%` }}
                      ></div>
                    </div>
                    
                    {/* Password requirements checklist */}
                    <ul className="mt-2 text-xs space-y-1">
                      <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-1">{formData.password.length >= 8 ? '✓' : '○'}</span>
                        At least 8 characters
                      </li>
                      <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-1">{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                        One lowercase letter
                      </li>
                      <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-1">{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                        One uppercase letter
                      </li>
                      <li className={`flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-1">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                        One number
                      </li>
                      <li className={`flex items-center ${SPECIAL_CHARS_REGEX.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-1">{SPECIAL_CHARS_REGEX.test(formData.password) ? '✓' : '○'}</span>
                        One special character
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`pl-10 pr-10 w-full rounded-md border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
              </div>

              {/* Cloudflare Turnstile verification - visible to users */}
              <div className="flex flex-col items-center">
                <Turnstile
                  ref={captchaRef}
                  size="normal"
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    // Clear any verification-related errors when successful
                    if (errors.captcha) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.captcha;
                        return newErrors;
                      });
                    }
                  }}
                  onError={() => {
                    setCaptchaToken(null);
                    setErrors(prev => ({ ...prev, captcha: 'Security verification failed. Please click the checkbox above to retry.' }));
                  }}
                  onExpire={() => {
                    setCaptchaToken(null);
                    setErrors(prev => ({ ...prev, captcha: 'Security verification expired. Please complete the verification above.' }));
                  }}
                />
                {errors.captcha && (
                  <p className={`mt-2 text-sm ${
                    errors.captcha.includes('complete the security verification')
                      ? 'text-yellow-600'
                      : 'text-red-500'
                  }`}>
                    {errors.captcha}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-wayja-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !captchaToken}
              >
                {loading ? 'Creating Account...' : !captchaToken ? 'Complete verification to create account' : 'Create Account'}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/auth/login" className="text-wayja-teal hover:text-wayja-teal-dark">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}